import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { ACCESS_TOKEN_STORAGE_KEY } from "@/lib/constants";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = not checked, false = guest, obj = authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip the /auth/me call entirely if no valid-shaped token is stored.
    // This both removes console noise on public pages and prevents an
    // injected garbage value from triggering a server roundtrip.
    let hasValidToken = false;
    if (typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
        hasValidToken = !!raw && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(raw);
        if (raw && !hasValidToken) localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      } catch { /* noop */ }
    }
    if (!hasValidToken) {
      setUser(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 on /auth/me is expected when token is stale — clear and fall back
      if (err?.response?.status && err.response.status !== 401) {
        console.error("Auth check failed:", formatApiError(err));
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.access_token);
      }
      setUser(data);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failures on logout are non-blocking — still clear local state
      console.warn("Logout request failed, clearing local session anyway:", formatApiError(err));
    }
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    setUser(false);
  };

  const hasRole = (...roles) => !!user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
